/**
 * @graphql cc
 */
import { graphql } from 'react-relay';
import { useContext } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { TEAM_ADMIN_CONTROLS_UPDATE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	useConfluenceSiteTeamConfigurationsQuery$data,
	useConfluenceSiteTeamConfigurationsQuery$variables,
} from './__generated__/useConfluenceSiteTeamConfigurationsQuery.graphql.ts';
import type {
	useConfluenceSiteTeamConfigurationsMutation$data,
	useConfluenceSiteTeamConfigurationsMutation$variables,
	ConfluenceTeamAdminControlsMode as GraphQLConfluenceTeamAdminControlsMode,
} from './__generated__/useConfluenceSiteTeamConfigurationsMutation.graphql.ts';
import { useAdminControlsFlags } from './useAdminControlsFlags.tsx';

// Runtime enum matching the GraphQL type
export enum ConfluenceTeamAdminControlsMode {
	ALL_TEAMS = 'ALL_TEAMS',
	VERIFIED_TEAMS = 'VERIFIED_TEAMS',
	NO_TEAMS = 'NO_TEAMS',
	MEMBER_MANAGED_TEAMS = 'MEMBER_MANAGED_TEAMS',
}

const ConfluenceSiteConfigurationQuery = graphql`
	query useConfluenceSiteTeamConfigurationsQuery {
		confluence {
			siteConfiguration {
				teamAdminControlsSettings {
					teamAdminControlsMode
				}
			}
		}
	}
`;

const UpdateTeamAdminControlsSettingsMutation = graphql`
	mutation useConfluenceSiteTeamConfigurationsMutation($input: ConfluenceTeamAdminControlsMode!) {
		confluence {
			updateTeamAdminControlsSettings(input: $input) {
				success
				errors {
					message
					extensions {
						statusCode
						errorType
						__typename
					}
					__typename
				}
				__typename
			}
		}
	}
`;

export const useConfluenceSiteTeamConfigurations = (): {
	siteConfigurationLoading: boolean;
	siteConfigurationError: ApolloErrorInterop | undefined;
	currentTeamAdminControlsMode: ConfluenceTeamAdminControlsMode | null | undefined;
	updateTeamAdminControlsSettings: (
		newTeamAdminControlsMode: ConfluenceTeamAdminControlsMode,
	) => Promise<void>;
	updateTeamAdminControlsSettingsLoading: boolean;
} => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { showErrorFlag, showSuccessFlag } = useAdminControlsFlags();
	const {
		data,
		loading: siteConfigurationLoading,
		error: siteConfigurationError,
	} = useQueryInterop<
		useConfluenceSiteTeamConfigurationsQuery$data,
		useConfluenceSiteTeamConfigurationsQuery$variables
	>(ConfluenceSiteConfigurationQuery, {
		fetchPolicy: 'network-only',
	});

	const [runMutation, { loading: updateTeamAdminControlsSettingsLoading }] = useMutationInterop<
		useConfluenceSiteTeamConfigurationsMutation$data,
		useConfluenceSiteTeamConfigurationsMutation$variables
	>(UpdateTeamAdminControlsSettingsMutation, {});

	async function updateTeamAdminControlsSettings(
		newTeamAdminControlsMode: ConfluenceTeamAdminControlsMode,
	): Promise<void> {
		await runMutation({
			variables: {
				input: newTeamAdminControlsMode as GraphQLConfluenceTeamAdminControlsMode,
			},
		}).catch((error) => {
			markErrorAsHandled(error);
			showErrorFlag();
			experienceTracker.fail({
				name: TEAM_ADMIN_CONTROLS_UPDATE_EXPERIENCE,
				error,
			});
		});
		showSuccessFlag();
		experienceTracker.succeed({
			name: TEAM_ADMIN_CONTROLS_UPDATE_EXPERIENCE,
		});
	}

	const currentTeamAdminControlsMode = data?.confluence?.siteConfiguration
		?.teamAdminControlsSettings?.teamAdminControlsMode as
		| ConfluenceTeamAdminControlsMode
		| null
		| undefined;

	return {
		siteConfigurationLoading,
		siteConfigurationError,
		currentTeamAdminControlsMode,
		updateTeamAdminControlsSettings,
		updateTeamAdminControlsSettingsLoading,
	};
};
